import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Sparkles, Mail, TrendingUp, BookOpen, ArrowRight } from 'lucide-react';
import SectionTitle from '../components/SectionTitle';
import { HorizontalCard, SmartImage } from '../components/ArticleCard';
import SEO from '../components/SEO';
import { useStories, normaliseStories } from '../hooks/useStories';

// ================= Skeleton =================
const Skel = ({ h = 'h-6', w = 'w-full', extra = '' }) => (
  <div className={`bg-[#efe8de] ${h} ${w} ${extra}`} />
);

// ================= HERO: L'HISTOIRE DU JOUR =================
// Grand format magazine plein largeur avec image à gauche + texte à droite
const HeroHistoireDuJour = ({ story, loading }) => {
  if (loading || !story) {
    return (
      <section className="max-w-[1280px] mx-auto px-4 md:px-8 pt-10 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 animate-pulse">
          <div className="md:col-span-7 aspect-[4/3] bg-[#efe8de]" />
          <div className="md:col-span-5 space-y-4 py-4">
            <Skel h="h-3" w="w-32" />
            <Skel h="h-10" />
            <Skel h="h-10" w="w-3/4" />
            <Skel h="h-4" />
            <Skel h="h-4" w="w-5/6" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-[1280px] mx-auto px-4 md:px-8 pt-10 pb-16">
      {/* Bandeau signature */}
      <div className="flex items-center gap-4 mb-6">
        <div className="h-[1px] flex-1 bg-[#c9a961]" />
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#c9a961] font-semibold">
          <Sparkles size={13} /> L&apos;Histoire du jour
        </div>
        <div className="h-[1px] flex-1 bg-[#c9a961]" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center">
        <Link to={`/article/${story.slug}`} className="md:col-span-7 group block relative aspect-[4/3] overflow-hidden bg-[#efe8de] shadow-lg">
          <SmartImage src={story.image} alt={story.title} />
          <span className="absolute top-4 left-4 bg-[#8b2c3f] text-white text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 z-10">
            {story.category}
          </span>
        </Link>
        <div className="md:col-span-5">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#8b2c3f] font-semibold mb-3">
            Récit exclusif — {story.date}
          </div>
          <h1
            className="text-[28px] md:text-[34px] leading-[1.15] font-bold text-[#1a1414] mb-5"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            <Link to={`/article/${story.slug}`} className="hover:text-[#8b2c3f] transition-colors">
              {story.title}
            </Link>
          </h1>
          <p
            className="text-[16px] leading-[1.65] text-[#3a3232] mb-6 italic"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {story.excerpt}
          </p>
          <div className="flex items-center gap-5 text-[12px] text-[#7a716c] mb-6">
            <span className="flex items-center gap-1.5">
              <Clock size={13} /> {story.read_time || '10 min'}
            </span>
            <span>Par <span className="text-[#1a1414] font-semibold">{story.author}</span></span>
          </div>
          <Link
            to={`/article/${story.slug}`}
            className="inline-flex items-center gap-2 bg-[#1a1414] hover:bg-[#8b2c3f] text-white text-[11px] uppercase font-semibold tracking-[0.2em] px-6 py-3 transition-colors group"
          >
            Lire l&apos;histoire
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ================= CETTE SEMAINE — grille asymétrique =================
const CetteSemaine = ({ stories, loading }) => (
  <section className="border-t border-[#e6ddd0]">
    <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-14">
      <SectionTitle accent="Cette semaine">TENDANCE</SectionTitle>

      {loading && stories.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 animate-pulse">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={i === 0 ? 'md:col-span-3 md:row-span-2' : 'md:col-span-3'}>
              <div className={`bg-[#efe8de] ${i === 0 ? 'aspect-[4/3]' : 'aspect-[16/10]'}`} />
              <div className="h-5 w-full bg-[#efe8de] mt-3" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          {/* Grosse story = 3 colonnes sur 2 lignes */}
          {stories[0] && (
            <article className="md:col-span-3 md:row-span-2 group">
              <Link to={`/article/${stories[0].slug}`} className="block relative aspect-[4/3] overflow-hidden bg-[#efe8de] mb-4">
                <SmartImage src={stories[0].image} alt={stories[0].title} />
                <span className="absolute top-3 left-3 bg-[#8b2c3f] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 z-10">
                  {stories[0].category}
                </span>
              </Link>
              <h3
                className="text-[22px] md:text-[24px] leading-tight font-bold text-[#1a1414] group-hover:text-[#8b2c3f] transition-colors"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                <Link to={`/article/${stories[0].slug}`}>{stories[0].title}</Link>
              </h3>
              <p className="mt-3 text-[14px] text-[#7a716c] line-clamp-2">{stories[0].excerpt}</p>
            </article>
          )}

          {/* 2 cartes moyennes empilées à droite */}
          {stories.slice(1, 3).map((p) => (
            <article key={p.id} className="md:col-span-3 group">
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-4">
                <Link to={`/article/${p.slug}`} className="block relative aspect-[4/3] sm:aspect-square overflow-hidden bg-[#efe8de]">
                  <SmartImage src={p.image} alt={p.title} />
                </Link>
                <div>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#8b2c3f] font-semibold">
                    {p.category}
                  </span>
                  <h3
                    className="mt-1.5 text-[17px] leading-snug font-bold text-[#1a1414] group-hover:text-[#8b2c3f] transition-colors line-clamp-3"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    <Link to={`/article/${p.slug}`}>{p.title}</Link>
                  </h3>
                  <div className="text-[11px] text-[#7a716c] mt-2 flex items-center gap-1.5">
                    <Clock size={11} /> {p.read_time || '10 min'}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  </section>
);

// ================= NEWSLETTER BANNER =================
const NewsletterBanner = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(''); // '', 'ok', 'err'

  const submit = (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) { setStatus('err'); return; }
    // Sauvegarde locale (à brancher sur Mailchimp/API plus tard)
    try {
      const list = JSON.parse(localStorage.getItem('bcool_newsletter') || '[]');
      if (!list.includes(email)) list.push(email);
      localStorage.setItem('bcool_newsletter', JSON.stringify(list));
    } catch (e) { /* ignore */ }
    setStatus('ok');
    setEmail('');
  };

  return (
    <section className="bg-[#1a1414] text-white">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-14 md:py-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#c9a961] font-semibold mb-4">
            <Mail size={13} /> La lettre du dimanche
          </div>
          <h2
            className="text-[32px] md:text-[42px] leading-[1.05] font-bold mb-4"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            Recevez <em className="text-[#c9a961] font-normal">une histoire</em>
            <br />chaque dimanche matin.
          </h2>
          <p className="text-[15px] text-white/70 max-w-md leading-relaxed">
            Une fois par semaine, une nouvelle chronique exclusive dans votre boîte mail —
            avant tout le monde, sans publicité, avec un peu plus de temps pour la lire.
          </p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <label className="text-[11px] uppercase tracking-[0.2em] text-white/60 block mb-2">
            Votre adresse email
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setStatus(''); }}
              placeholder="votre@email.fr"
              className="flex-1 bg-transparent border border-white/30 focus:border-[#c9a961] outline-none px-4 py-3 text-white placeholder:text-white/40"
              required
            />
            <button
              type="submit"
              className="bg-[#c9a961] hover:bg-[#b6934c] text-[#1a1414] font-bold text-[12px] uppercase tracking-[0.2em] px-6 py-3 transition-colors"
            >
              M&apos;abonner
            </button>
          </div>
          {status === 'ok' && (
            <p className="text-[13px] text-[#c9a961]">
              ✓ Merci ! Votre inscription est bien enregistrée.
            </p>
          )}
          {status === 'err' && (
            <p className="text-[13px] text-[#e88b8b]">Email invalide.</p>
          )}
          <p className="text-[10px] text-white/40 mt-2">
            Désinscription en un clic. Vos données ne sont jamais partagées.
          </p>
        </form>
      </div>
    </section>
  );
};

// ================= L'ESSENTIEL — 3 colonnes équilibrées =================
const LEssentiel = ({ derniers = [], populaires = [], decouvrir = [], loading }) => {

  const Column = ({ title, icon, items, tint = '#8b2c3f' }) => (
    <div>
      <div className="flex items-center gap-2 border-b-2 border-[#1a1414] pb-3 mb-5">
        <span style={{ color: tint }}>{icon}</span>
        <h3 className="text-[13px] uppercase tracking-[0.22em] font-bold text-[#1a1414]">
          {title}
        </h3>
      </div>
      <div className="space-y-5">
        {items.length === 0 && loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse grid grid-cols-[70px_1fr] gap-3">
              <div className="aspect-square bg-[#efe8de]" />
              <div className="space-y-2">
                <div className="h-3 w-full bg-[#efe8de]" />
                <div className="h-3 w-3/4 bg-[#efe8de]" />
              </div>
            </div>
          ))
        ) : (
          items.map((p, idx) => (
            <article key={p.id} className="group grid grid-cols-[70px_1fr] gap-3">
              <Link to={`/article/${p.slug}`} className="relative block aspect-square overflow-hidden bg-[#efe8de]">
                <SmartImage src={p.image} alt={p.title} hover={false} />
                {items === populaires && (
                  <span className="absolute top-1 left-1 bg-[#1a1414]/90 text-[#c9a961] text-[10px] font-bold w-5 h-5 flex items-center justify-center">
                    {idx + 1}
                  </span>
                )}
              </Link>
              <div>
                <span className="text-[9px] uppercase tracking-[0.18em] font-semibold" style={{ color: tint }}>
                  {p.category}
                </span>
                <h4
                  className="mt-1 text-[13px] leading-snug font-bold text-[#1a1414] group-hover:text-[#8b2c3f] transition-colors line-clamp-3"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  <Link to={`/article/${p.slug}`}>{p.title}</Link>
                </h4>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );

  return (
    <section className="border-t border-[#e6ddd0]">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-14">
        <div className="text-center mb-10">
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#c9a961] font-semibold mb-2">
            L&apos;essentiel
          </div>
          <h2
            className="text-[32px] md:text-[38px] font-bold text-[#1a1414]"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            À ne <em>surtout</em> pas manquer
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <Column title="Derniers récits" icon={<BookOpen size={15} />} items={derniers} />
          <Column title="Les plus lus" icon={<TrendingUp size={15} />} items={populaires} tint="#c9a961" />
          <Column title="À découvrir" icon={<Sparkles size={15} />} items={decouvrir} tint="#3a3232" />
        </div>
      </div>
    </section>
  );
};

// ================= CHRONIQUES RECENTES =================
const ChroniquesRecentes = ({ stories, loading, onLoadMore, canLoadMore }) => (
  <section className="border-t border-[#e6ddd0] bg-[#f4ede1]/40">
    <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-14">
      <SectionTitle accent="En intégralité">CHRONIQUES</SectionTitle>
      <div className="space-y-8">
        {loading && stories.length === 0 ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-5 border-b border-[#e6ddd0] pb-8 animate-pulse">
              <div className="aspect-[16/10] bg-[#efe8de]" />
              <div className="space-y-3">
                <div className="h-3 w-32 bg-[#efe8de]" />
                <div className="h-6 w-full bg-[#efe8de]" />
                <div className="h-4 w-3/4 bg-[#efe8de]" />
              </div>
            </div>
          ))
        ) : (
          stories.map((p) => <HorizontalCard key={p.id} post={p} />)
        )}
      </div>
      {canLoadMore && (
        <div className="pt-10 text-center">
          <button
            onClick={onLoadMore}
            className="inline-flex items-center gap-2 border border-[#1a1414] text-[#1a1414] hover:bg-[#1a1414] hover:text-white text-[11px] uppercase font-semibold tracking-[0.24em] px-8 py-3 transition-colors"
          >
            Voir plus de chroniques <ArrowRight size={14} />
          </button>
        </div>
      )}
    </div>
  </section>
);

// ================= APPEL À CONTRIBUTIONS =================
const AppelContributions = () => (
  <section className="border-t border-[#e6ddd0]">
    <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-16 text-center">
      <div className="text-[11px] uppercase tracking-[0.4em] text-[#c9a961] font-semibold mb-3">
        Votre voix compte
      </div>
      <h2
        className="text-[32px] md:text-[42px] font-bold text-[#1a1414] mb-4 max-w-3xl mx-auto leading-[1.15]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        Vous avez <em>une histoire</em> à partager&nbsp;?
      </h2>
      <p className="text-[15px] text-[#3a3232] max-w-2xl mx-auto leading-relaxed mb-8"
         style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
        Nous lisons chaque récit envoyé par nos lectrices et lecteurs. Les plus touchants
        sont publiés — anonymement si vous le souhaitez, toujours avec votre accord.
      </p>
      <a
        href="mailto:tonywahab234@gmail.com?subject=Mon histoire pour BcoolStore"
        className="inline-flex items-center gap-2 bg-[#8b2c3f] hover:bg-[#6d1f2f] text-white text-[11px] uppercase font-semibold tracking-[0.24em] px-8 py-3 transition-colors"
      >
        Envoyer mon récit <ArrowRight size={14} />
      </a>
    </div>
  </section>
);

// ================= HOME =================
const Home = () => {
  const { data: raw, loading, error } = useStories({ limit: 100, order: 'desc' });
  const stories = normaliseStories(raw);
  const [displayCount, setDisplayCount] = useState(4);

  // Seed unique par session → l'ordre de la sidebar change à chaque rechargement
  const [seed] = React.useState(() => Date.now());

  const heroStory = stories[0] || null;
  const tendance = stories.slice(1, 4);

  // "L'essentiel" : 3 colonnes distinctes, avec shuffle pour "À découvrir"
  const stats = React.useMemo(() => {
    const derniers = stories.slice(0, 4);
    const populaires = stories.slice(0, 4); // les 4 plus récentes affichées comme "top"
    // À découvrir : sélection aléatoire de 4 histoires hors derniers/populaires
    const excluded = new Set([...derniers, ...populaires].map((s) => s.id));
    const pool = stories.filter((s) => !excluded.has(s.id));
    // Shuffle Fisher-Yates
    const shuffled = [...pool];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    const decouvrir = shuffled.slice(0, 4);
    return { derniers, populaires, decouvrir };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stories.length, seed]);

  const chroniques = stories.slice(4, 4 + displayCount);
  const canLoadMore = stories.length > 4 + displayCount;

  if (error) {
    return (
      <main className="max-w-[1280px] mx-auto px-4 md:px-8 py-16">
        <p className="text-[#8b2c3f] text-center">
          Impossible de charger les articles. Veuillez rafraîchir la page.
        </p>
      </main>
    );
  }

  return (
    <main>
      <SEO
        title={null}
        description="Récits vécus, chroniques familiales et confidences françaises — BcoolStore raconte les histoires qui donnent envie de tourner la page."
        url="/"
      />
      <HeroHistoireDuJour story={heroStory} loading={loading} />
      <CetteSemaine stories={tendance} loading={loading} />
      <NewsletterBanner />
      <LEssentiel
        derniers={stats.derniers}
        populaires={stats.populaires}
        decouvrir={stats.decouvrir}
        loading={loading}
      />
      <ChroniquesRecentes
        stories={chroniques}
        loading={loading}
        onLoadMore={() => setDisplayCount((c) => c + 4)}
        canLoadMore={canLoadMore}
      />
      <AppelContributions />
    </main>
  );
};

export default Home;
